import React from "react";

import FileXIcon from "@uui/FileX03";
import { isEmpty } from "lodash/fp";
import { DateTime } from "luxon";

import Typography from "@mui/material/Typography";

import BillingModuleError from "@app/analytics/account/Billing/components/BillingModuleError";
import { BillingCard } from "@app/analytics/account/Billing/components/styles";
import useBillingHistory from "@app/analytics/account/Billing/hooks/useBillingHistory";
import Table from "@app/analytics/components/Table";
import TableBody from "@app/analytics/components/Table/TableBody";
import TableCell from "@app/analytics/components/Table/TableCell";
import TableHead from "@app/analytics/components/Table/TableHead";
import TableRow from "@app/analytics/components/Table/TableRow";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useDataTable, { HeadCell } from "@app/common/hooks/useDataTable";
import type { Invoice } from "@app/common/types";
import { formatCurrencyFromCents } from "@app/utils/utils";

import {
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateSubtext,
  EmptyStateTitle,
} from "@rui/EmptyStateContainer";
import Link from "@rui/Link";
import Stack, { Row } from "@rui/Stack";

import { LABEL_FOR_PLAN } from "../constants";
import { getPaymentStatus } from "../utils";

const HEAD_CELLS: HeadCell<Invoice>[] = [
  {
    id: "created",
    label: "Invoice Date",
    sortable: true,
  },
  {
    id: "description",
    label: "Description",
    sortable: false,
  },
  {
    id: "quantity",
    label: "Quantity",
    align: "right",
    sortable: false,
  },
  {
    id: "amountDue",
    label: "Invoice Amount",
    align: "right",
    sortable: false,
  },
  {
    id: "paid",
    label: "Status",
    align: "right",
    sortable: false,
  },
];

const isErrorStatus = (status: string) =>
  status === "Failed" || status === "Error";

const canPayInvoice = (invoice: Invoice, currentSubscriptionId?: string) => {
  // invoices can only be paid if they:
  // 1) have a URL
  // 2) have not already been paid, and
  // 3) are for the currently active subscription (or are not for a specific subscription,
  //    in the case of manual invoices)
  return (
    invoice.hostedInvoiceUrl != null &&
    !invoice.paid &&
    (!invoice.stripeSubscriptionId ||
      invoice.stripeSubscriptionId === currentSubscriptionId)
  );
};

const EmptyState = ({ isError }: { isError: boolean }) => {
  return (
    <TableRow>
      <TableCell colSpan={HEAD_CELLS.length}>
        {isError ? (
          <BillingModuleError
            entityLabel="billing history"
            sx={{ minHeight: "150px" }}
            type="overlay"
          />
        ) : (
          <EmptyStateContainer sx={{ minHeight: "150px" }}>
            <EmptyStateIcon Icon={FileXIcon} />
            <EmptyStateTitle>You don't have any invoices yet</EmptyStateTitle>
            <EmptyStateSubtext>
              Invoices will appear here as they become available
            </EmptyStateSubtext>
          </EmptyStateContainer>
        )}
      </TableCell>
    </TableRow>
  );
};

const BillingHistory = () => {
  const { subscription } = useBillingContext();
  const { invoices, isLoading, isError } = useBillingHistory();

  const { sortedData, handleSort, order, orderBy } = useDataTable(
    invoices,
    HEAD_CELLS
  );

  return (
    <Stack>
      <Row variant="apart" padding={1.5}>
        <Typography variant="bodyPrimaryMed">Billing History</Typography>
      </Row>
      <Stack sx={{ overflowX: "auto" }}>
        <BillingCard sx={{ minHeight: 0 }} testId="billing-billing-history">
          <Table borderless>
            <TableHead
              headCells={HEAD_CELLS}
              onSort={handleSort}
              order={order}
              orderBy={orderBy}
              sx={{ backgroundColor: "transparent.black.2" }}
            />
            <TableBody
              loading={isLoading}
              sx={{
                "& .MuiTableRow-root .MuiTableCell-root": {
                  height: "0px",
                },
              }}
            >
              {isEmpty(sortedData) || isError ? (
                <EmptyState isError={isError} />
              ) : (
                sortedData.map((invoice) => {
                  const status = getPaymentStatus(invoice);
                  return (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        {DateTime.fromSeconds(invoice.created).toFormat("DD")}
                      </TableCell>
                      <TableCell>
                        {invoice.plan
                          ? `${LABEL_FOR_PLAN[invoice.plan]} Plan ${
                              invoice.billingCadence
                                ? `(${invoice.billingCadence})`
                                : ""
                            }`
                          : invoice.description}
                      </TableCell>
                      <TableCell align="right">{invoice.quantity}</TableCell>
                      <TableCell align="right">
                        {formatCurrencyFromCents(invoice.amountDue)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          display="inline"
                          color={
                            isErrorStatus(status) ? "error.main" : undefined
                          }
                          sx={{ font: "inherit" }}
                          noWrap
                        >
                          {status}
                        </Typography>
                        {canPayInvoice(
                          invoice,
                          subscription.stripeSubscriptionId
                        ) ? (
                          <>
                            {" ("}
                            <Link
                              href={invoice.hostedInvoiceUrl!}
                              fontWeight={400}
                              underline
                            >
                              Pay Invoice
                            </Link>
                            )
                          </>
                        ) : invoice.invoicePdf ? (
                          <>
                            {" ("}
                            <Link
                              href={invoice.invoicePdf}
                              fontWeight={400}
                              underline
                            >
                              Invoice
                            </Link>
                            {invoice.receiptUrl && (
                              <>
                                {" "}
                                |{" "}
                                <Link
                                  href={invoice.receiptUrl}
                                  fontWeight={400}
                                  underline
                                >
                                  Receipt
                                </Link>
                              </>
                            )}
                            )
                          </>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </BillingCard>
      </Stack>
    </Stack>
  );
};

export default BillingHistory;
